import {
  Box,
  Button,
  Chip,
  Link,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import React from "react";
import {
  CustomDetailsList,
  CustomeDialog,
  CustomTable,
  DeleteModal,
  Loader,
  PageListLayout,
} from "../../../shared";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { useAdminUsers } from "../hooks";
import { createColumnHelper } from "@tanstack/react-table";
import {
  AddRoleDialog,
  DeleteRoleFromUserModal,
  MacidSecurityDialog,
} from "../components";
import { useRef, useState } from "react";
import SimpleReactValidator from "simple-react-validator";

export const AdminUsersDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [, forceUpdate] = useState();
  const validator = useRef(
    new SimpleReactValidator({ autoForceUpdate: { forceUpdate: forceUpdate } })
  );
  const {
    state,
    onDelete,
    userDetails,
    resetDeleteInfo,
    confirmDelete,
    deleteInfo,
    handleModal,
    removeRoleFromUser,
    onRoleAssign,
    onChangeRole,
    rolesListQuery,
    handleDisableMacid,
    handleSecurityDialogOpen,
    onSubmitMacidSecurity,
    isDeletable,
    isRoleCreateVisible,
    isRoleDeletable,
    isRoleEditable,
  } = useAdminUsers({
    userId: id,
    isList: false,
  });
  const breadcrumbs = [
    <Link
      underline="none"
      key="1"
      color="#1760D2"
      onClick={(e) => navigate("/manage-users")}
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      Manage Users
    </Link>,
    <Typography
      key="2"
      color="inherit"
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      {state?.data?.name}
    </Typography>,
  ];

  const columnHelper = createColumnHelper();

  const columns = [
    {
      title: "Name",
      accessor: "name",
    },
    {
      title: "Email",
      accessor: "email",
    },
    {
      title: "Time Zone",
      accessor: "timezone",
    },
  ];

  const columns2 = [
    columnHelper.accessor("display_name", {
      header: () => "Store Name",
    }),
  ];
  const columns3 = [
    columnHelper.accessor("display_name", {
      header: () => "Role Name",
    }),
    columnHelper.accessor("actions", {
      header: () => "",
      align: "right",
      cell: ({ row }) => {
        return (
          <Stack
            justifyContent="end"
            sx={{ cursor: "pointer", width: "fit-content", ml: "auto" }}
            onClick={() =>
              handleModal(
                "delete-role",
                true,
                row.original.id,
                row.original.display_name
              )
            }
          >
            {isRoleDeletable && <img alt="delete" src="/icons/ic_trash.svg" />}
          </Stack>
        );
      },
    }),
  ];

  const submit = () => {
    if (validator.current.allValid()) {
      onRoleAssign();
    } else {
      validator.current.showMessages();
      forceUpdate(1);
    }
  };
  return (
    <PageListLayout
      headerText={
        <Stack alignItems="center" direction="row" gap="12px">
          <Typography variant="h6" fontWeight={600} fontSize="24px">
            {userDetails?.data?.name}
          </Typography>
          {userDetails.data?.status ? (
            <Chip
              size="small"
              color="primary"
              variant="filled"
              label="ACTIVE"
            />
          ) : (
            <Chip size="small" color="gray" label="INACTIVE" />
          )}
        </Stack>
      }
      rightContent={
        isDeletable && (
          <img
            alt="delete"
            onClick={(e) => onDelete(id)}
            src="/icons/ic_trash.svg"
            style={{ cursor: "pointer", width: "24px" }}
          />
        )
      }
      breadcrumbs={breadcrumbs}
      isChanged={false}
      sx={{
        backgroundColor: "#ffffff",
        p: 0,
      }}
    >
      {userDetails?.isLoading ? (
        <Stack
          minHeight="calc(100vh - 120px)"
          alignContent="center"
          justifyContent="center"
        >
          <Loader />
        </Stack>
      ) : (
        <Stack
          sx={{
            borderRadius: "4px",
            bgcolor: "#fff",
            px: "16px",
            gap: "20px",
          }}
        >
          <CustomDetailsList
            columns={columns}
            data={userDetails?.data}
            title="Basic Details"
          />

          <Box
            sx={{
              padding: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: "8px",
              bgcolor: "#F5F8FC",
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Box>
                <Typography fontWeight={600} fontSize="16px">
                  MAC ID Security
                </Typography>
                <Typography fontWeight={400} fontSize="14px">
                  Disabling MAC ID security will allow this user to access the
                  Recipe App from any MAC ID.
                </Typography>
              </Box>
            </Box>

            <Switch
              color="primary"
              checked={state?.mac_security}
              onClick={(e) =>
                state?.mac_security
                  ? handleSecurityDialogOpen(true)
                  : handleDisableMacid(e, true)
              }
              disabled={!isRoleEditable}
            />
          </Box>

          <CustomTable
            defaultData={userDetails?.data?.role}
            columns={columns3}
            isPagination={false}
            headerText="Roles"
            showSearchBar={false}
            emptyTableMessage={"No roles assigned"}
            rightContent={
              isRoleCreateVisible && (
                <Button
                  sx={{
                    border: "1px solid #BCC7DB",
                    bgcolor: "#F5F8FC",
                  }}
                  type="button"
                  size="small"
                  variant="outlined"
                  color="gray"
                  startIcon={<AddIcon />}
                  onClick={() => handleModal("add-role", true)}
                >
                  Role
                </Button>
              )
            }
            tableHeaderStyles={{ px: 0, fontSize: "18px", fontWeight: 600 }}
            enableTableContainerPaddingBottom
          />

          <CustomTable
            defaultData={
              userDetails?.data?.stores?.length
                ? userDetails?.data?.stores.sort((a, b) =>
                    a.display_name?.localeCompare(b?.display_name)
                  )
                : []
            }
            columns={columns2}
            isPagination={false}
            headerText="Stores"
            rightContent=" "
            tableHeaderStyles={{ px: 0, fontSize: "18px", fontWeight: 600 }}
            enableTableContainerPaddingBottom
          />
        </Stack>
      )}
      <CustomeDialog
        title="Add Role"
        PaperProps={{
          sx: {
            minWidth: "482px",
            minHeight: "200px",
          },
        }}
        open={state?.addRoleOpen}
        CloseIcons={false}
        divider={false}
        content={
          <AddRoleDialog
            onChange={onChangeRole}
            state={state}
            options={rolesListQuery.data ?? []}
            validator={validator}
          />
        }
        footer={{
          display: true,
          onConfirm: submit,
          onCancel: () => handleModal("add-role", false),
        }}
      />

      <DeleteModal
        open={deleteInfo?.show}
        handleClose={resetDeleteInfo}
        onConfirm={confirmDelete}
      />

      <DeleteRoleFromUserModal
        open={state?.deleteRoleOpen}
        handleClose={() => handleModal("delete-role", false, null)}
        onConfirm={removeRoleFromUser}
        userName={userDetails?.data?.name}
        display_name={state?.display_name}
      />

      <CustomeDialog
        PaperProps={{
          sx: {
            width: { sm: "500px", xs: "90%" },
          },
        }}
        closeIcon={false}
        open={state?.isDialogOpen}
        title={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            px="20px"
            pt="20px"
            gap="12px"
          >
            <img src="/icons/ic_pair_dialog.svg" alt="pair icon" />
            <Typography fontWeight={500} fontSize={20}>
              Disable MAC ID Security?
            </Typography>
          </Stack>
        }
        content={
          <MacidSecurityDialog
            onSubmit={onSubmitMacidSecurity}
            handleSecurityDialogOpen={handleSecurityDialogOpen}
          />
        }
      />
    </PageListLayout>
  );
};
